import React, { useEffect, useState } from "react";

import { transactionService } from "../../services/transactionService";

interface CallInsuranceConsentRequestModalProps {
  locale: "th" | "en";
  onClose: () => void;
}

const CallInsuranceConsentRequestModal: React.FC<
  CallInsuranceConsentRequestModalProps
> = ({ locale, onClose }) => {
  const [title, setTitle] = useState("");
  const [bodyHeader, setBodyHeader] = useState("");
  const [bodyContent, setBodyContent] = useState("");
  const [cancelLabel, setCancelLabel] = useState("Cancel");
  const [confirmLabel, setConfirmLabel] = useState("Confirm");
  const [processingLabel, setProcessingLabel] = useState("Processing...");
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingAction, setProcessingAction] = useState<
    "accept" | "reject" | ""
  >("");

  useEffect(() => {
    if (locale === "th") {
      setTitle(
        "หนังสือยอมรับการตรวจรักษาทั่วไปและหนังสือแสดงความยินยอมการเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคล"
      );
      setBodyHeader(
        "ข้าพเจ้ารับทราบถึงหน้าที่ของโรงพยาบาลสมิติเวช (สุขุมวิท และโรงพยาบาลสมิติเวช ศรีนครินทร์)* ซึ่งประกอบกิจการโดยบริษัท สมิติเวช จำกัด (มหาชน) (เรียกรวมกันว่า “สถานพยาบาล”) ในเรื่องต่างๆดังนี้"
      );
      setBodyContent(`
        <strong>2. ความยินยอมในการเก็บรวบรวม ใช้ และเปิดเผยข้อมูลส่วนบุคคลเพื่อวัตถุประสงค์ต่างๆ ดังนี้<br>
        2.1 เพื่อใช้สิทธิเรียกค่าสินไหมทดแทนจากบริษัทประกันภัยหรือใช้สิทธิเบิกค่ารักษาพยาบาลจากบุคคลที่สามซึ่งเป็นบุคคลหรือนิติบุคคลทั้งในประเทศไทยและต่างประเทศ</strong>
        <p>ข้าพเจ้ายินยอมให้สถานพยาบาลเปิดเผย และ/หรือส่งสำเนาข้อมูลส่วนบุคคลของข้าพเจ้าที่มีอยู่กับสถานพยาบาลให้บริษัทประกันภัย 
        หรือผู้ให้บริการบริหารจัดการสินไหมทดแทนของบริษัทประกันภัยนั้น เพื่อการปฏิบัติตามสัญญาที่ข้าพเจ้าหรือสถานพยาบาลได้ทำหรือจะทำไว้กับบริษัทประกันภัย 
        รวมถึงบุคคลที่สามตามที่ข้าพเจ้าหรือสถานพยาบาลมีสัญญาต่อกัน หรือบุคคล นิติบุคคลหรือหน่วยงานไม่ว่าภาครัฐ เอกชน หรือรัฐวิสาหกิจทั้งในประเทศไทยและต่างประเทศ
        ที่เป็นผู้ส่งข้าพเจ้ามาตรวจรักษากับสถานพยาบาลหรือเป็นผู้ชำระค่าบริการตรวจรักษาของข้าพเจ้า</p>
      `);
      setCancelLabel("ไม่ยินยอม");
      setConfirmLabel("ยินยอม");
      setProcessingLabel("กำลังดำเนินการ...");
    } else {
      setTitle(
        "ACKNOWLEDGEMENT FOR GENERAL INVESTIGATIONS AND TREATMENTS AND CONSENT FOR COLLECTION, STORAGE, USAGE AND DISCLOSURE OF PERSONAL DATA"
      );
      setBodyHeader(
        "I hereby, acknowledge and consent (where applicable) Samitivej Hospital (Sukhumvit)* altogether named “the Hospital” on the followings:"
      );
      setBodyContent(`
        <strong>1.2. Obligation to protect and secure patient’s personal health information and reasonably disclose the information as deem necessary.</strong>
        <p>I voluntarily give permission to physicians, nurses, medical personnel and/or other staffs in the team of the Hospital to investigate, treat or perform other practices in accordance with professional principles 
        for my benefit of investigation and treatments for my illness. I have adequately been informed and well understood about health information related to the investigation and treatments as well Declaration of Patient’s Right,
        including receiving a copy of Patient’s Rights and Patient’s Responsibilities.</p>
      `);
      setCancelLabel("I do not agree (refuse)");
      setConfirmLabel("I agree (consent)");
      setProcessingLabel("Processing...");
    }
  }, [locale]);

  const setInsuranceConsent = async (patientUseInsurance: boolean) => {
    const jwtSession = transactionService.getJwtSession();
    if (!jwtSession || !jwtSession.transactionId) {
      throw new Error("No valid transaction context found");
    }

    await transactionService.setInsuranceConsent({
      transactionId: jwtSession.transactionId,
      patientUseInsurance,
    });
  };

  const handleAction = async (action: "accept" | "reject") => {
    setIsProcessing(true);
    setProcessingAction(action);
    try {
      await setInsuranceConsent(action === "accept");
    } catch (error) {
      console.error(error);
      alert("An error occurred while setting insurance consent: " + error);
    } finally {
      setIsProcessing(false);
      onClose();
    }
  };

  return (
    <div
      id="modal"
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50"
    >
      <div className="bg-white max-w-3xl w-[90vw] max-h-[80vh] rounded-lg overflow-y-auto shadow-lg">
        {/* Header */}
        <div className="border-b px-6 py-4">
          <h4 id="modalHeader" className="text-lg font-semibold">
            {title}
          </h4>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4 overflow-y-auto">
          <p id="modalBodyHeader" className="text-gray-700">
            {bodyHeader}
          </p>
          <div
            id="modalBodyContent"
            className="text-gray-700 prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: bodyContent }}
          />
        </div>

        {/* Footer */}
        <div className="border-t flex justify-end gap-3 px-6 py-4">
          <button
            id="modalCancelButton"
            disabled={isProcessing}
            onClick={() => handleAction("reject")}
            className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300 disabled:opacity-50"
          >
            {isProcessing && processingAction === "reject" ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" /> {processingLabel}
              </span>
            ) : (
              cancelLabel
            )}
          </button>

          <button
            id="modalConfirmButton"
            disabled={isProcessing}
            onClick={() => handleAction("accept")}
            className="px-4 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {isProcessing && processingAction === "accept" ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" /> {processingLabel}
              </span>
            ) : (
              confirmLabel
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CallInsuranceConsentRequestModal;
